/**
 * voice-v8 — Vorlesen an der Abbruchstelle fortsetzen (19.10.2026).
 *
 * Martin: Wird ein langer Text vorgelesen und unterbrochen (neuer Zug, Mikro, Wachhund, Seite weg), soll „Vorlesen“
 * dort weitermachen — nicht von vorn. Diese Datei ist EINE Stelle für:
 *   - die Spur, die die TtsQueue je fertig gespieltem Stück fortschreibt (`FortsetzSpur`),
 *   - Speichern/Laden über playback-resume.ts (localStorage, nur dieses Gerät; Version = Text-Stempel),
 *   - die reinen Rechnungen für Hinweis und Abschnitte der Vorlese-Karte.
 *
 * Der Versatz zählt in ZEICHEN des Sprechtextes (was die Schlange bekommt), nicht in Sekunden: Die Stimme wird je Stück
 * vertont, es gibt keine Gesamtdauer. Gemerkt wird das Ende des zuletzt GANZ gespielten Stücks — ein Stück, das mitten
 * im Satz abbrach, beginnt beim Fortsetzen wieder an seinem Anfang (an einer Satzgrenze, nie mitten im Wort).
 */
import { clearResume, contentVersion, loadResume, saveResume } from "./playback-resume.js";

/** Ab so vielen Zeichen merkt sich die Schlange eine Position (kürzere Texte hört man in Sekunden neu). */
export const FORTSETZEN_MIN_ZEICHEN = 400;
/** Ab so vielen Zeichen zeigt eine Bot-Antwort die Vorlese-Karte mit Schieber (Martin: „etwa 1.500“). */
export const LANGER_TEXT_ZEICHEN = 1500;
/** Ein Abschnitt des Schiebers hat mindestens so viele Zeichen (kürzere Absätze werden zusammengefasst). */
export const ABSCHNITT_MIN_ZEICHEN = 300;
/** Ab so wenigen Zeichen Rest gilt ein Text als zu Ende gehört (kein „Weiter ab“ für den letzten Halbsatz). */
export const REST_ENDE_ZEICHEN = 40;
/** So viele Zeichen des Textes zeigt der Hinweis „Weiter ab …“. */
export const HINWEIS_ZEICHEN = 28;

const ID_PRAEFIX = "vorlesen:";
const INDEX_SCHLUESSEL = "v8-resume-index:vorlesen";
const INDEX_MAX = 40;

/** Kennung der Nachricht: Anfang des Textes — bleibt gleich, auch wenn der Text hinten geändert wird. */
export function fortsetzKennung(sprechText: string): string {
  return ID_PRAEFIX + contentVersion(sprechText.slice(0, 200));
}

/** Version: ganzer Text — ändert er sich, beginnt das Vorlesen von vorn (playback-resume.ts). */
export function fortsetzVersion(sprechText: string): string {
  return contentVersion(sprechText);
}

export interface FortsetzZiel {
  id: string;
  version: string;
}

export function fortsetzZiel(sprechText: string): FortsetzZiel {
  return { id: fortsetzKennung(sprechText), version: fortsetzVersion(sprechText) };
}

function leseIndex(): string[] {
  try {
    const roh = localStorage.getItem(INDEX_SCHLUESSEL);
    const a = roh ? (JSON.parse(roh) as unknown) : [];
    return Array.isArray(a) ? a.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function schreibeIndex(ids: string[]): void {
  try {
    localStorage.setItem(INDEX_SCHLUESSEL, JSON.stringify(ids));
  } catch {
    /* fail-soft */
  }
}

/** Ereignis am Fenster: eine Schlange hat die Stelle eines Textes fortgeschrieben (`id` = Kennung, `versatz` 0 = zu Ende gehört). */
export const EREIGNIS_GEMERKT = "voice-v8:vorlese-gemerkt";

function meldeGemerkt(id: string, versatz: number): void {
  try {
    window.dispatchEvent(new CustomEvent(EREIGNIS_GEMERKT, { detail: { id, versatz } }));
  } catch {
    /* ohne window (Tests ohne DOM) */
  }
}

/**
 * Darf der Abschluss eines alten Stroms den Knopf auf „idle“ setzen und die Pille verstecken? (A1, Prüfer 19.10.)
 * Nein, sobald schon ein ANDERER Strom die aktuelle Schlange ist (Sprung des Schiebers, „Von vorn“): der alte endet erst
 * NACH dem Start des neuen und würde sonst dessen Zustand überschreiben.
 */
export function darfKnopfZuruecksetzen(aktuelleSchlange: unknown, eigene: unknown): boolean {
  return aktuelleSchlange === null || aktuelleSchlange === undefined || aktuelleSchlange === eigene;
}

/** Position speichern; die ältesten Einträge über `INDEX_MAX` werden gelöscht (localStorage wächst nicht ohne Ende). */
export function merkeVersatz(ziel: FortsetzZiel, versatz: number): void {
  saveResume(ziel.id, ziel.version, versatz);
  const index = leseIndex().filter((i) => i !== ziel.id);
  index.push(ziel.id);
  while (index.length > INDEX_MAX) {
    const alt = index.shift();
    if (alt) clearResume(alt);
  }
  schreibeIndex(index);
  meldeGemerkt(ziel.id, versatz);
}

/** Gemerkter Versatz für diesen Sprechtext — null: keiner, geänderter Text, oder fast am Ende. */
export function ladeVersatz(sprechText: string): number | null {
  const ziel = fortsetzZiel(sprechText);
  const v = loadResume(ziel.id, ziel.version);
  if (v === null) return null;
  if (!Number.isFinite(v) || v < 1 || v > sprechText.length - REST_ENDE_ZEICHEN) return null;
  return Math.floor(v);
}

export function loescheVersatz(sprechText: string): void {
  const ziel = fortsetzZiel(sprechText);
  clearResume(ziel.id);
  schreibeIndex(leseIndex().filter((i) => i !== ziel.id));
  meldeGemerkt(ziel.id, 0);
}

/**
 * Die Spur einer Schlange: Wo steht der Text, den sie bekam? Die Schlange füttert `push` mit jedem Stück Sprechtext und
 * ruft `stueckFertig` mit dem Text jedes GANZ gespielten Stücks — in der Reihenfolge des Abspielens.
 */
export class FortsetzSpur {
  private text = "";
  private cursor = 0;
  private readonly ziel: FortsetzZiel;
  /** Der Text, der der Schlange vorgelegt wurde, beginnt bei diesem Zeichen des ganzen Sprechtextes. */
  constructor(
    private readonly basis: number,
    gesamtText: string,
    private readonly beiStand?: (versatz: number) => void,
  ) {
    this.ziel = fortsetzZiel(gesamtText);
  }

  push(chunk: string): void {
    this.text += chunk;
  }

  /** Versatz im ganzen Sprechtext (nach dem letzten fertigen Stück). */
  versatz(): number {
    return this.basis + this.cursor;
  }

  stueckFertig(stueck: string): void {
    const s = stueck.trim();
    if (!s) return;
    const i = this.text.indexOf(s, this.cursor);
    // Nicht gefunden (Stück wurde umgeformt): die Länge zählt — lieber etwas zu früh als zu spät.
    this.cursor = i >= 0 ? i + s.length : Math.min(this.text.length, this.cursor + s.length);
    const v = this.versatz();
    merkeVersatz(this.ziel, v);
    this.beiStand?.(v);
  }

  /** Alles gehört (kein Abbruch): Eintrag löschen, nächstes Mal von vorn. */
  ende(): void {
    clearResume(this.ziel.id);
    schreibeIndex(leseIndex().filter((i) => i !== this.ziel.id));
    meldeGemerkt(this.ziel.id, 0);
    this.beiStand?.(0);
  }
}

/** Hinweistext „Weiter ab „…“ — die ersten Zeichen ab dem Versatz, an der Wortgrenze gekürzt. */
export function hinweisAb(sprechText: string, versatz: number): string {
  const rest = sprechText.slice(versatz).replace(/\s+/g, " ").trim();
  if (rest.length <= HINWEIS_ZEICHEN) return `Weiter ab „${rest}“`;
  const kurz = rest.slice(0, HINWEIS_ZEICHEN);
  const sp = kurz.lastIndexOf(" ");
  return `Weiter ab „${(sp > 10 ? kurz.slice(0, sp) : kurz).trimEnd()} …“`;
}

/** Soll diese Antwort die Vorlese-Karte mit Schieber bekommen? */
export function sollKarte(sprechTextLaenge: number): boolean {
  return sprechTextLaenge >= LANGER_TEXT_ZEICHEN;
}

/**
 * Die Anfänge der Abschnitte (Schieber-Schritte): an Absatzgrenzen, sonst an Satzenden; jeder Abschnitt hat mindestens
 * `ABSCHNITT_MIN_ZEICHEN`. Der erste Anfang ist immer 0.
 */
export function abschnittsStarts(text: string, min: number = ABSCHNITT_MIN_ZEICHEN): number[] {
  const starts = [0];
  const grenze = /\n\s*\n|(?<=[.!?…])\s+(?=\S)/g;
  let m: RegExpExecArray | null;
  while ((m = grenze.exec(text)) !== null) {
    const naechster = m.index + m[0].length;
    if (naechster - (starts[starts.length - 1] ?? 0) >= min && text.length - naechster >= REST_ENDE_ZEICHEN) starts.push(naechster);
  }
  return starts;
}

/** In welchem Abschnitt liegt der Versatz? (Index in `starts`) */
export function abschnittVon(starts: number[], versatz: number): number {
  let idx = 0;
  for (let i = 0; i < starts.length; i++) if ((starts[i] ?? 0) <= versatz) idx = i;
  return idx;
}
